import { DARK_THEME_MODES, type ThemeMode } from '@constants/colors';

/**
 * Shell styling for the modals that open on top of the login page (MFA verify,
 * MFA setup, add device).
 *
 * They used to take `themeColors.card`, which is the app's *content* surface:
 * on horizon-night that is a green-grey, so the dialog landed as a warm slab
 * on top of the cool Scanner backdrop and the glass form. These give it the
 * same material as the showcase panel instead -- translucent brand ink, a real
 * backdrop blur, a hairline rim and a lit top edge.
 *
 * Pair it with `<div className="login-glass" data-login-surface={surface}>`
 * around the modal body so the controls inside pick up the same input styling
 * the login form uses (see login-form-glass.css). The wrapper is needed
 * because Mantine portals modal content out of the form's subtree.
 */
export const loginGlassModal = (mode: ThemeMode) => {
  const isDark = DARK_THEME_MODES.includes(mode);

  return {
    isDark,
    /** Feed to the body wrapper's data-login-surface. */
    surface: (isDark ? 'dark' : 'light') as 'dark' | 'light',
    /** Primary and secondary ink, matching --lg-text in login-form-glass.css. */
    text: isDark ? '#eef2f6' : '#0f2a44',
    textMuted: isDark ? 'rgba(238, 242, 246, 0.68)' : 'rgba(15, 42, 68, 0.68)',
    /** Brand accent and its halo — the same blue the login form focuses with. */
    accent: '#2563eb',
    accentHalo: isDark ? 'rgba(37, 99, 235, 0.22)' : 'rgba(37, 99, 235, 0.12)',
    overlayProps: {
      backgroundOpacity: isDark ? 0.62 : 0.45,
      blur: 6,
    },
    styles: {
      content: {
        backgroundColor: isDark ? 'rgba(11, 28, 43, 0.86)' : 'rgba(255, 255, 255, 0.86)',
        WebkitBackdropFilter: 'blur(18px) saturate(120%)',
        backdropFilter: 'blur(18px) saturate(120%)',
        // Rim as an inset shadow rather than a border, so the dialog keeps its
        // exact size and Mantine's own layout maths still hold.
        border: 'none',
        boxShadow: isDark
          ? '0 48px 96px -40px rgba(2, 9, 17, 0.8), inset 0 0 0 1px rgba(255, 255, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.16)'
          : '0 48px 96px -40px rgba(15, 42, 68, 0.3), inset 0 0 0 1px rgba(15, 42, 68, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.7)',
        color: isDark ? '#eef2f6' : '#0f2a44',
      },
      header: { backgroundColor: 'transparent' },
    },
  };
};
